import { useEffect, useRef } from 'react';

/**
 * Spec for matching a single keystroke.
 *
 * - `key` and `keys` are mutually exclusive — pass exactly one. Letter keys
 *   match case-insensitively (`'c'` matches both `'c'` and `'C'`).
 * - `meta: true` requires either `metaKey` or `ctrlKey` (cross-platform Cmd/Ctrl).
 *   `meta: false` (or omitted) requires neither.
 * - `shift: true | false` requires that exact shift state. `shift: undefined`
 *   matches both. The `alt` flag works the same way.
 */
export interface ShortcutSpec {
  key?: string;
  keys?: readonly string[];
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
}

export interface ShortcutOptions {
  /** Optional gate evaluated after key/modifier match, before the handler runs. */
  condition?: () => boolean;
  /** When true, the hook calls `event.preventDefault()` after a match (and after the condition passes). */
  preventDefault?: boolean;
}

function matchesKey(eventKey: string, spec: ShortcutSpec): boolean {
  if (spec.keys) {
    return spec.keys.some((k) => keysEqual(eventKey, k));
  }
  if (spec.key !== undefined) {
    return keysEqual(eventKey, spec.key);
  }
  return true;
}

function keysEqual(a: string, b: string): boolean {
  // Letter keys (single chars) match case-insensitively. Named keys ('Escape',
  // 'ArrowLeft', etc.) match exactly — those are always >1 char anyway.
  if (a.length === 1 && b.length === 1) {
    return a.toLowerCase() === b.toLowerCase();
  }
  return a === b;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.isContentEditable
  );
}

/**
 * Primitive for window-level keyboard shortcuts. Centralises the
 * "ignore-when-editing" guard, modifier-key matching, and stable ref-wrapped
 * handler that every canvas shortcut hook used to copy by hand.
 *
 * The handler is captured into a ref so it can change between renders without
 * re-binding the listener.
 */
export function useKeyboardShortcut(
  spec: ShortcutSpec,
  handler: (event: KeyboardEvent) => void,
  options: ShortcutOptions = {},
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const conditionRef = useRef(options.condition);
  conditionRef.current = options.condition;

  const preventDefault = options.preventDefault ?? false;

  // Snapshot spec into a ref so handler mutations across renders don't re-bind
  // the listener. Spec values are primitives — the simple shallow snapshot
  // here is fine for our call sites.
  const specRef = useRef(spec);
  specRef.current = spec;

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      const s = specRef.current;
      if (isEditableTarget(e.target)) return;
      if (!matchesKey(e.key, s)) return;
      const metaOrCtrl = e.metaKey || e.ctrlKey;
      if (s.meta === true && !metaOrCtrl) return;
      if (s.meta === false && metaOrCtrl) return;
      if (s.shift === true && !e.shiftKey) return;
      if (s.shift === false && e.shiftKey) return;
      if (s.alt === true && !e.altKey) return;
      if (s.alt === false && e.altKey) return;

      const condition = conditionRef.current;
      if (condition && !condition()) return;

      if (preventDefault) e.preventDefault();
      handlerRef.current(e);
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [preventDefault]);
}
